import { Link } from 'react-router-dom';
import LogoIconDecoration from '../components/LogoIconDecoration';

export default function About() {
  return (
    <div className="pt-[73px]">
      {/* Header Section */}
      <section className="bg-primary text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/img-0230.jpg"
            alt="About Background"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 text-center space-y-4">
          <span className="text-accent text-xs font-bold tracking-widest uppercase">About Site Facility</span>
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-extrabold tracking-tight text-white">
            Danish Enterprise – Global Scale
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Site Facility stands at the forefront of specialized accommodations management for large employee groups in remote locations worldwide.
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <LogoIconDecoration className="w-[600px] h-[600px] opacity-[0.015] -right-32 top-24 rotate-45" />
        <LogoIconDecoration className="w-[500px] h-[500px] opacity-[0.015] -left-24 bottom-24 -rotate-12" />
        <div className="mx-auto max-w-5xl px-6 md:px-12 space-y-16 relative z-10">
          {/* History / Intro */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7 space-y-6">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-primary">Our Story & Core Values</h2>
              <p className="text-slate-600 leading-relaxed">
                Site Facility, a Danish enterprise founded in 2008 by Jakob Heskjær, stands at the forefront of specialized accommodations management for large employee groups in remote locations worldwide. Our distinguished service, aptly named Camp Anywhere, represents our commitment to meeting diverse project needs with unparalleled efficiency.
              </p>
              <p className="text-slate-600 leading-relaxed">
                At Site Facility, we are dedicated to making our clients’ daily lives more manageable. Our unwavering commitment to exceptional service is anchored in our core values of trustworthiness and excellence, permeating every facet of our operations.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Since our inception, these values have been the guiding force behind our endeavors. While our headquarters proudly reside in Denmark, our operational footprint extends far beyond, encompassing Scandinavia and numerous other countries. This global reach reflects not only the success of our unique Camp Anywhere service but also our steadfast dedication to delivering excellence in accommodations management.
              </p>
              <p className="text-slate-600 leading-relaxed font-medium text-primary">
                At Site Facility, our history is a testament to the enduring principles that shape our identity: a commitment to exceptional service, a foundation of trustworthiness, and an unwavering pursuit of excellence. Join us in redefining accommodations management on a global scale.
              </p>
            </div>
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100/80 space-y-6">
                <h3 className="text-xs font-bold text-accent uppercase tracking-widest border-b border-slate-100 pb-4">
                  Why Choose Us
                </h3>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                      <i className="fa-regular fa-user-tie-hair text-lg" />
                    </div>
                    <div>
                      <h4 className="font-bold text-primary text-sm">ONE POINT OF CONTACT</h4>
                      <p className="text-xs text-slate-500 mt-1">Streamlined, clear communication to minimize misunderstandings.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                      <i className="fa-regular fa-clock text-lg" />
                    </div>
                    <div>
                      <h4 className="font-bold text-primary text-sm">LESS TRAVEL TIME</h4>
                      <p className="text-xs text-slate-500 mt-1">Strategic placement of accommodations close to your project site.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                      <i className="fa-regular fa-shield-check text-lg" />
                    </div>
                    <div>
                      <h4 className="font-bold text-primary text-sm">INCREASED SAFETY</h4>
                      <p className="text-xs text-slate-500 mt-1">Certified, fully approved facilities and smooth daily operations.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Customized Accommodations Redefined */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center pt-8 border-t border-slate-100">
            <div className="relative rounded-2xl overflow-hidden shadow-lg aspect-video lg:aspect-square">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/bild-1-1.jpg"
                alt="Customized Accommodations"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-primary">Customized Accommodations Redefined</h2>
              <p className="text-slate-600 leading-relaxed">
                Site Facility stands apart by eschewing one-size-fits-all solutions. Instead, we meticulously analyze each client’s accommodation needs, adopting a tailored approach to ensure the utmost cost-effectiveness. Our process commences with a thorough research phase, delving into the intricacies of the project area to design a bespoke accommodation solution that precisely aligns with project requirements.
              </p>
              <p className="text-slate-600 leading-relaxed">
                In instances where projects unfold in remote locations, distant from urban centers, a dedicated camp emerges as the most pragmatic choice. This solution optimizes proximity and resource utilization for enhanced efficiency. Conversely, when the project site boasts ample hotel capacity, our adept hotel management comes into play seamlessly. We ensure that our clients always enjoy unfettered access to the requisite number of rooms, simplifying the accommodation process while maintaining cost-effectiveness.
              </p>
              <p className="text-slate-600 leading-relaxed">
                At Site Facility, our commitment to innovative, client-centric solutions is unwavering. By shunning standard approaches and embracing a dynamic methodology, we redefine the landscape of accommodations management. Experience the Site Facility difference, where every solution is a bespoke answer to your unique project demands.
              </p>
            </div>
          </div>

          {/* Sustainability in Every Solution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center pt-8 border-t border-slate-100">
            <div className="space-y-6 order-2 lg:order-1">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-primary">Sustainability in Every Solution</h2>
              <p className="text-slate-600 leading-relaxed">
                At Site Facility, our dedication to sustainability extends beyond our innovative accommodation solutions to encompass the broader impact on the environment and the well-being of project participants.
              </p>
              <p className="text-slate-600 leading-relaxed">
                By meticulously tailoring our approach to each client’s needs, we not only optimize cost-effectiveness but also champion environmental responsibility. One significant aspect of our sustainability commitment is minimizing the need for extensive travel. Our strategic placement of accommodations, especially in remote locations, reduces the daily commute for workers. This not only enhances their quality of life but also significantly lowers the carbon footprint associated with daily travel, contributing to a more sustainable work environment.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Furthermore, our use of modular constructions showcases our commitment to resource efficiency. These modules are optimized for factors like heat, cold, and electricity usage, ensuring minimal environmental impact. By utilizing these purpose-built structures, we not only reduce construction waste but also create energy-efficient spaces that align with modern sustainability standards.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Our practices, combined with thoughtful accommodation placement and the use of advanced modules, represent a holistic approach to sustainability. It’s not just about offering cost-effective solutions; it’s about fostering a work environment that prioritizes both the well-being of the workforce and the planet.
              </p>
              <p className="text-slate-600 leading-relaxed font-medium text-accent">
                At Site Facility, sustainability is a cornerstone of our operational ethos. Experience accommodations that not only optimize project efficiency but also reflect our unwavering commitment to a sustainable future, where every decision is a step towards a greener, more responsible world.
              </p>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-lg aspect-video lg:aspect-square order-1 lg:order-2">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/6b13c390-5e11-4ba2-a04f-8e3fde27ab67/moduler-scaled.jpg"
                alt="Sustainability & Modular Construction"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white text-center relative overflow-hidden">
        <div className="relative z-10 mx-auto max-w-5xl px-6 space-y-6">
          <h2 className="text-xl sm:text-2xl md:text-4xl font-bold tracking-tight text-white">
            Want to learn more about our history?
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Get in touch with us today to discuss your project requirements or to hear more about our specialized accommodations management.
          </p>
          <div className="pt-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 py-4 px-8 rounded-xl bg-accent text-white font-semibold hover:bg-accent-light transition-all-300 shadow-md hover:shadow-lg"
            >
              <span>Get In Touch</span>
              <i className="fa-regular fa-paper-plane text-sm" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
